import { useRef, useState } from "react";
import { useSnackbar } from "notistack";
import moment from "moment";
import Icon from "../../../../components/HOC/Icon";
import ocrTravelerDocument from "../../../../controllers/Flight/ocrTravelerDocument";
import { normalizeCountryCodeToAlpha2 } from "./countryCode";
import {
  sanitizePassengerName,
  splitGivenNames,
} from "../../../../utils/booking/sanitizePassengerName";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

function formatOcrDate(value) {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
}

function mapOcrGender(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (
    normalized === "male" ||
    normalized === "m" ||
    normalized === "mr"
  ) {
    return "Male";
  }
  if (
    normalized === "female" ||
    normalized === "f" ||
    normalized === "ms" ||
    normalized === "mrs" ||
    normalized === "miss"
  ) {
    return "Female";
  }
  if (value === "Male" || value === "Female") return value;
  return "";
}

function getEntity(entities, type) {
  return entities.find(
    (entity) => String(entity?.type || "").toLowerCase() === type.toLowerCase(),
  );
}

function unwrapOcrPayload(payload) {
  if (!payload) return {};
  if (payload.traveler || payload.entities) return payload;
  if (payload.data?.traveler || payload.data?.entities) return payload.data;
  return payload;
}

export function mapOcrTravelerDocument(ocrData, currentData = {}) {
  const source = unwrapOcrPayload(ocrData);
  const traveler = source?.traveler || {};
  const doc = traveler.document || {};
  const entities = source?.entities || [];

  const pick = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "");

  const rawNationality = pick(
    entities[0]?.mentionText,
    doc.nationality,
    doc.issuanceCountry,
    doc.validityCountry,
    currentData.document?.nationality,
  );
  const nationality = normalizeCountryCodeToAlpha2(rawNationality);

  const rawFirstName = pick(
    traveler.firstName,
    getEntity(entities, "firstName")?.mentionText,
    currentData.firstName,
  );
  const rawMiddleName = pick(
    traveler.middleName,
    getEntity(entities, "middleName")?.mentionText,
    currentData.middleName,
  );
  const { firstName, middleName } = splitGivenNames(rawFirstName, rawMiddleName);

  const lastName = sanitizePassengerName(
    pick(traveler.lastName, getEntity(entities, "lastName")?.mentionText) ||
      currentData.lastName,
  );

  return {
    ...currentData,
    firstName,
    middleName,
    lastName,
    gender: mapOcrGender(
      pick(
        traveler.gender,
        getEntity(entities, "Sex")?.mentionText,
        currentData.gender,
      ),
    ),
    birthDate: formatOcrDate(
      pick(
        traveler.birthDate,
        getEntity(entities, "dateOfBirth")?.normalizedText,
        getEntity(entities, "dateOfBirth")?.mentionText,
        currentData.birthDate,
      ),
    ),
    document: {
      ...currentData.document,
      documentType:
        pick(doc.documentType, currentData.document?.documentType) || "Passport",
      number: pick(
        doc.number,
        getEntity(entities, "PassportNo")?.mentionText,
        currentData.document?.number,
      ),
      nationality,
      issuanceCountry: normalizeCountryCodeToAlpha2(
        pick(doc.issuanceCountry, nationality),
      ),
      validityCountry: normalizeCountryCodeToAlpha2(
        pick(doc.validityCountry, nationality),
      ),
      issuanceDate: formatOcrDate(
        pick(
          doc.issuanceDate,
          getEntity(entities, "dateOfIssue")?.normalizedText,
          currentData.document?.issuanceDate,
        ),
      ),
      expiryDate: formatOcrDate(
        pick(
          doc.expiryDate,
          getEntity(entities, "dateOfExpiry")?.normalizedText,
          getEntity(entities, "dateOfExpiry")?.mentionText,
          currentData.document?.expiryDate,
        ),
      ),
      holder: true,
    },
  };
}

export default function BookPassengerDocumentScan({ onExtracted, disabled }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  async function processFile(file) {
    if (!file || disabled || loading) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      enqueueSnackbar("Please upload a JPEG, PNG, or WEBP image.", {
        variant: "warning",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("File must be 5MB or smaller.", { variant: "warning" });
      return;
    }

    setFileName(file.name);
    setOpen(true);
    setLoading(true);

    const res = await ocrTravelerDocument(file);
    setLoading(false);

    if (res.return) {
      onExtracted?.(res.data);
      enqueueSnackbar("Document scanned successfully.", { variant: "success" });
      return;
    }

    setFileName("");
    enqueueSnackbar(res.msg || "Could not scan document.", { variant: "error" });
  }

  function handleInputChange(event) {
    const file = event.target.files?.[0];
    processFile(file);
    event.target.value = "";
  }

  function handleDrop(event) {
    event.preventDefault();
    processFile(event.dataTransfer.files?.[0]);
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-lg border border-[#DBDBDB] bg-white px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 transition-colors w-fit"
      >
        <Icon icon="mdi:passport" className="w-5 h-5" />
        <span>Scan passport or ID</span>
        <Icon
          icon={open ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="w-5 h-5 text-[#6D7C94]"
        />
      </button>

      {open ? (
        <div
          className={`rounded-lg border border-dashed p-6 flex flex-col items-center justify-center gap-3 text-center transition-colors ${
            disabled || loading
              ? "border-[#DBDBDB] bg-slate-50 cursor-not-allowed opacity-70"
              : "border-[#DBDBDB] bg-white cursor-pointer hover:border-primary/40 hover:bg-primary/5"
          }`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !disabled && !loading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleInputChange}
            disabled={disabled || loading}
          />

          <Icon icon="mdi:cloud-upload-outline" className="w-10 h-10 text-primary/70" />

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-primary">
              {loading
                ? "Scanning document..."
                : "Drop passport or ID here, or click to upload"}
            </span>
            <span className="text-xs text-[#6D7C94]">
              JPEG, PNG, or WEBP up to 5MB
            </span>
            {fileName ? (
              <span className="text-xs text-primary/80">{fileName}</span>
            ) : null}
          </div>

          {loading ? (
            <div className="w-full max-w-xs h-2 rounded-full bg-primary/10 overflow-hidden">
              <div className="h-full w-1/2 rounded-full bg-primary animate-pulse" />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
